export const FULL_PHOTO_MAX_DIMENSION = 1600;
export const FULL_PHOTO_TARGET_BYTES = 1.4 * 1024 * 1024;
export const THUMBNAIL_MAX_DIMENSION = 384;
export const THUMBNAIL_TARGET_BYTES = 140 * 1024;
export const SOURCE_PHOTO_MAX_BYTES = 25 * 1024 * 1024;

const JPEG_QUALITIES = [0.82, 0.76, 0.7, 0.64, 0.58, 0.5, 0.42] as const;
const DIMENSION_REDUCTION = 0.82;
const MAX_DIMENSION_PASSES = 5;

export type PreparedImageUpload = {
  uploadId: string;
  photo: File;
  thumbnail: File;
};

type DecodedImage = {
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
};

const uploadIds = new WeakMap<File, string>();
const preparedUploads = new WeakMap<File, Promise<PreparedImageUpload>>();

function uploadIdFor(file: File): string {
  const existing = uploadIds.get(file);
  if (existing) return existing;
  const created = crypto.randomUUID();
  uploadIds.set(file, created);
  return created;
}

function outputName(file: File, suffix = "") {
  const stem = file.name.replace(/\.[^.]+$/, "").trim() || "photo";
  return `${stem}${suffix}.jpg`;
}

function canvasToJPEG(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob || blob.type.toLowerCase() !== "image/jpeg") {
        reject(new Error("This browser could not encode the photo as JPEG."));
        return;
      }
      resolve(blob);
    }, "image/jpeg", quality);
  });
}

async function encodeBoundedJPEG(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  maxDimension: number,
  targetBytes: number,
): Promise<Blob> {
  const sourceLongestEdge = Math.max(sourceWidth, sourceHeight);
  let longestEdge = Math.min(maxDimension, sourceLongestEdge);
  let smallest: Blob | null = null;

  for (let dimensionPass = 0; dimensionPass < MAX_DIMENSION_PASSES; dimensionPass++) {
    const scale = longestEdge / sourceLongestEdge;
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    try {
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Canvas is not available.");
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      // JPEG has no alpha channel. Composite transparent PNG/WebP pixels onto
      // white explicitly instead of relying on browser-specific transparent
      // canvas serialization (which commonly produces a black background).
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.drawImage(source, 0, 0, width, height);

      for (const quality of JPEG_QUALITIES) {
        const blob = await canvasToJPEG(canvas, quality);
        if (!smallest || blob.size < smallest.size) smallest = blob;
        if (blob.size <= targetBytes) return blob;
      }
    } finally {
      // Clearing the backing store matters on mobile when several camera
      // photos are selected. Do not leave each unsuccessful encode pass for
      // the garbage collector to release at an unspecified time.
      canvas.width = 1;
      canvas.height = 1;
    }

    longestEdge = Math.max(1, Math.round(longestEdge * DIMENSION_REDUCTION));
  }

  if (smallest && smallest.size <= targetBytes) return smallest;
  throw new Error("This photo could not be compressed enough. Please choose a less detailed image.");
}

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        close: () => bitmap.close(),
      };
    } catch {
      // Safari can decode some camera formats through an image element even
      // when createImageBitmap does not support that container.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  const image = document.createElement("img");
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Image decoding failed"));
      image.src = objectUrl;
    });
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
}

/**
 * Decode once, strip metadata through canvas rendering, and emit only the two
 * private JPEG variants accepted by the API. The original file is never sent.
 */
export function validateSourcePhoto(file: File): void {
  // Some mobile camera/file providers omit the MIME type. Decoding remains
  // the authoritative client-side check, and only re-encoded JPEGs reach the
  // API, so an absent type is safe to try while an explicit non-image is not.
  if (file.type && !file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  if (file.size > SOURCE_PHOTO_MAX_BYTES) {
    throw new Error("Please choose a photo smaller than 25 MB.");
  }
}

async function prepareImageUploadFileUncached(file: File): Promise<PreparedImageUpload> {
  let decoded: DecodedImage;
  try {
    decoded = await decodeImage(file);
  } catch {
    throw new Error("Could not decode this photo. Please choose a JPG, PNG, WebP, or HEIC image.");
  }

  try {
    if (!decoded.width || !decoded.height) {
      throw new Error("This photo has invalid dimensions.");
    }

    const [photo, thumbnail] = await Promise.all([
      encodeBoundedJPEG(
        decoded.source,
        decoded.width,
        decoded.height,
        FULL_PHOTO_MAX_DIMENSION,
        FULL_PHOTO_TARGET_BYTES,
      ),
      encodeBoundedJPEG(
        decoded.source,
        decoded.width,
        decoded.height,
        THUMBNAIL_MAX_DIMENSION,
        THUMBNAIL_TARGET_BYTES,
      ),
    ]);

    return {
      uploadId: uploadIdFor(file),
      photo: new File([photo], outputName(file), {
        type: "image/jpeg",
        lastModified: Date.now(),
      }),
      thumbnail: new File([thumbnail], outputName(file, "-thumbnail"), {
        type: "image/jpeg",
        lastModified: Date.now(),
      }),
    };
  } finally {
    decoded.close();
  }
}

export async function prepareImageUploadFile(file: File): Promise<PreparedImageUpload> {
  validateSourcePhoto(file);

  const existing = preparedUploads.get(file);
  if (existing) return await existing;

  // Selection previews and form submission can overlap. Cache the work by the
  // immutable File object so the original is decoded and compressed only once.
  const preparation = prepareImageUploadFileUncached(file);
  preparedUploads.set(file, preparation);
  void preparation.catch(() => {
    if (preparedUploads.get(file) === preparation) {
      preparedUploads.delete(file);
    }
  });
  return await preparation;
}
